/**
 * MembersScreen — admin Members list (Stage 3.3a).
 *
 * Secretary / coordinator workflow:
 *   - See every primary member, filterable by status (Pending / Active /
 *     Lapsed / Suspended) with summary counts in the tabs.
 *   - Search across name, email, phone, payment reference.
 *   - Per-row actions:
 *       • Pending  → Approve
 *       • Active   → Suspend (prompts for reason — required for audit log)
 *       • Lapsed   → Reactivate (after dues paid)
 *       • Suspended → Reactivate
 *
 * urql handles caching; after a mutation we re-execute the list query so
 * the row's status badge updates immediately.
 */
import { useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, RotateCcw, Search, ShieldAlert, Trash2, UsersRound } from "lucide-react";
import { useMutation, useQuery } from "urql";

import { useAuth } from "../auth/AuthProvider";
import { cn } from "../lib/cn";
import { askForReason, confirmDestructive, showError, showSuccess } from "../lib/sweet";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const MEMBERS_QUERY = /* GraphQL */ `
  query AdminMembers($status: StatusEnum, $search: String, $limit: Int, $offset: Int) {
    memberCounts {
      total
      pending
      active
      lapsed
      suspended
      totalDependents
      totalPeople
    }
    members(status: $status, search: $search, limit: $limit, offset: $offset) {
      id
      firstName
      lastName
      email
      phone
      postcode
      status
      paymentReference
      duesPaidUntil
      createdAt
      dependents {
        id
        firstName
        lastName
        relationship
        dateOfBirth
        phone
      }
    }
  }
`;

const APPROVE_MEMBER = /* GraphQL */ `
  mutation ApproveMember($id: ID!) {
    approveMember(memberId: $id) { id status }
  }
`;

const SUSPEND_MEMBER = /* GraphQL */ `
  mutation SuspendMember($id: ID!, $reason: String!) {
    suspendMember(memberId: $id, reason: $reason) { id status }
  }
`;

const REACTIVATE_MEMBER = /* GraphQL */ `
  mutation ReactivateMember($id: ID!) {
    reactivateMember(memberId: $id) { id status }
  }
`;

const DELETE_MEMBER = /* GraphQL */ `
  mutation DeleteMember($id: ID!) {
    deleteMember(memberId: $id)
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// GraphQL enum wire format uses the uppercase NAME, not the value. Both the
// `status` filter argument we send AND the `status` field we receive use
// these exact strings ("PENDING_VERIFICATION", not "pending_verification").
type StatusValue = "PENDING_VERIFICATION" | "ACTIVE" | "LAPSED" | "SUSPENDED";

interface DependentRow {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string | null;
  dateOfBirth: string | null;
  phone: string | null;
}

interface MemberRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  postcode: string | null;
  status: StatusValue;
  paymentReference: string | null;
  duesPaidUntil: string | null;
  createdAt: string;
  dependents: DependentRow[];
}

interface MembersResp {
  memberCounts: {
    total: number;
    pending: number;
    active: number;
    lapsed: number;
    suspended: number;
    totalDependents: number;
    totalPeople: number;
  };
  members: MemberRow[];
}

// Filter tabs — order matters (Pending first so admins see what's waiting on them)
const TABS: Array<{ key: "all" | StatusValue; label: string; countKey?: keyof MembersResp["memberCounts"] }> = [
  { key: "all", label: "All", countKey: "total" },
  { key: "PENDING_VERIFICATION", label: "Pending", countKey: "pending" },
  { key: "ACTIVE", label: "Active", countKey: "active" },
  { key: "LAPSED", label: "Lapsed", countKey: "lapsed" },
  { key: "SUSPENDED", label: "Suspended", countKey: "suspended" },
];

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function MembersScreen() {
  const { hasRole } = useAuth();
  const canDelete = hasRole("coordinator");

  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]["key"]>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const [{ data, fetching, error }, refetchList] = useQuery<MembersResp>({
    query: MEMBERS_QUERY,
    variables: {
      status: activeTab === "all" ? null : activeTab,
      search: search || null,
      limit: 50,
      offset: 0,
    },
    requestPolicy: "cache-and-network",
  });

  const [, approveMember] = useMutation(APPROVE_MEMBER);
  const [, suspendMember] = useMutation(SUSPEND_MEMBER);
  const [, reactivateMember] = useMutation(REACTIVATE_MEMBER);
  const [, deleteMember] = useMutation(DELETE_MEMBER);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  function gqlMessage(err: { message: string }): string {
    return err.message.replace(/^\[GraphQL\]\s*/, "");
  }

  async function handleApprove(id: string) {
    setActionError(null);
    const result = await approveMember({ id });
    if (result.error) {
      await showError({ title: "Couldn't approve", text: gqlMessage(result.error) });
      return;
    }
    refetchList({ requestPolicy: "network-only" });
    showSuccess({ title: "Approved" });
  }

  async function handleSuspend(member: MemberRow) {
    setActionError(null);
    const name = `${member.firstName} ${member.lastName}`.trim() || member.email || "this member";
    const reason = await askForReason({
      title: `Suspend ${name}?`,
      text: "The reason is recorded in the audit log and shown to the secretary later.",
      placeholder: "e.g. Repeated code-of-conduct violations",
      confirmText: "Suspend member",
    });
    if (reason === null) return;
    const result = await suspendMember({ id: member.id, reason });
    if (result.error) {
      await showError({ title: "Couldn't suspend", text: gqlMessage(result.error) });
      return;
    }
    refetchList({ requestPolicy: "network-only" });
    showSuccess({ title: "Suspended" });
  }

  async function handleReactivate(id: string) {
    setActionError(null);
    const result = await reactivateMember({ id });
    if (result.error) {
      await showError({ title: "Couldn't reactivate", text: gqlMessage(result.error) });
      return;
    }
    refetchList({ requestPolicy: "network-only" });
    showSuccess({ title: "Reactivated" });
  }

  async function handleDelete(member: MemberRow) {
    setActionError(null);
    const name = `${member.firstName} ${member.lastName}`.trim() || member.email || "this member";

    // If the primary has dependents, they cascade-delete on the backend
    // (Member.primary_member is ON DELETE CASCADE). Spell out who else
    // vanishes so the coordinator confirms the FULL household removal,
    // not just the primary they clicked on.
    const familyCount = member.dependents.length;
    let warning =
      "This removes their member profile, login account, and any dependents. " +
      "It cannot be undone. Members with recorded payments can't be deleted.";
    if (familyCount > 0) {
      const list = member.dependents
        .map((d) => `• ${d.firstName} ${d.lastName}`.trim())
        .join("\n");
      warning =
        `${name} has ${familyCount} family member${familyCount === 1 ? "" : "s"} ` +
        `who will ALSO be removed:\n\n${list}\n\n` +
        "This deletes the whole household: profile, login, and all dependents. " +
        "It cannot be undone.";
    }

    const ok = await confirmDestructive({
      title: familyCount > 0 ? `Delete ${name} and ${familyCount} family member${familyCount === 1 ? "" : "s"}?` : `Delete ${name}?`,
      text: warning,
      confirmText: familyCount > 0 ? "Yes, delete the whole household" : "Yes, delete permanently",
    });
    if (!ok) return;
    const result = await deleteMember({ id: member.id });
    if (result.error) {
      await showError({ title: "Couldn't delete", text: gqlMessage(result.error) });
      return;
    }
    refetchList({ requestPolicy: "network-only" });
    showSuccess({
      title: familyCount > 0 ? "Household deleted" : "Deleted",
    });
  }

  const counts = data?.memberCounts;

  return (
    <div className="space-y-4">
      {/* Headline stats — community headcount including dependents */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total people"
          value={counts?.totalPeople ?? 0}
          hint="Primaries + family members"
          accent="primary"
        />
        <StatCard
          label="Primary members"
          value={counts?.total ?? 0}
          hint="One per household"
        />
        <StatCard
          label="Family members"
          value={counts?.totalDependents ?? 0}
          hint="Dependents under a primary"
        />
      </div>

      {/* Filter tabs with counts */}
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => {
          const count = tab.countKey ? data?.memberCounts?.[tab.countKey] : undefined;
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                isActive
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50",
              )}
            >
              <span>{tab.label}</span>
              {count !== undefined && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-xs",
                    isActive ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, phone, payment reference…"
            className="w-full rounded-md border border-stone-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
          />
        </div>
        <button
          type="submit"
          className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
        >
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearchInput(""); setSearch(""); }}
            className="rounded-md px-3 py-2 text-sm text-stone-500 hover:text-stone-700"
          >
            Clear
          </button>
        )}
      </form>

      {/* Errors */}
      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          Couldn't load members: {error.message}
        </div>
      )}
      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 text-red-500 hover:text-red-700"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
        <table className="min-w-full divide-y divide-stone-200">
          <thead className="bg-stone-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-stone-500">
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Payment ref</Th>
              <Th>Family</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {fetching && !data ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-stone-500">
                  <Loader2 className="mx-auto size-5 animate-spin" />
                </td>
              </tr>
            ) : data?.members?.length ? (
              data.members.map((member) => (
                <MemberRowView
                  key={member.id}
                  member={member}
                  canDelete={canDelete}
                  expanded={expandedIds.has(member.id)}
                  onToggleExpanded={() => toggleExpanded(member.id)}
                  onApprove={() => handleApprove(member.id)}
                  onSuspend={() => handleSuspend(member)}
                  onReactivate={() => handleReactivate(member.id)}
                  onDelete={() => handleDelete(member)}
                />
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-stone-500">
                  No members match this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer hint */}
      {data && data.members.length === 50 && (
        <p className="text-center text-xs text-stone-500">
          Showing first 50 results. Refine the search or status filter to see fewer / more.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row + sub-components
// ---------------------------------------------------------------------------

function MemberRowView({
  member,
  canDelete,
  expanded,
  onToggleExpanded,
  onApprove,
  onSuspend,
  onReactivate,
  onDelete,
}: {
  member: MemberRow;
  canDelete: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onApprove: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const familyCount = member.dependents.length;
  return (
    <>
      <tr className={cn("text-sm text-stone-700", expanded && "bg-stone-50")}>
        <Td>
          <div className="font-medium text-stone-900">
            {member.firstName} {member.lastName}
          </div>
          <div className="text-xs text-stone-500">
            Joined {new Date(member.createdAt).toLocaleDateString()}
          </div>
        </Td>
        <Td>{member.email ?? <span className="text-stone-400">—</span>}</Td>
        <Td>{member.phone ?? <span className="text-stone-400">—</span>}</Td>
        <Td>
          {member.paymentReference ? (
            <span className="font-mono text-xs text-stone-700">{member.paymentReference}</span>
          ) : (
            <span className="text-stone-400">—</span>
          )}
        </Td>
        <Td>
          {familyCount > 0 ? (
            <button
              type="button"
              onClick={onToggleExpanded}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium transition-colors",
                expanded
                  ? "bg-stone-200 text-stone-800"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200",
              )}
              aria-expanded={expanded}
              aria-controls={`family-${member.id}`}
            >
              {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
              <UsersRound className="size-3.5" />
              <span>{familyCount}</span>
            </button>
          ) : (
            <span className="text-xs text-stone-400">—</span>
          )}
        </Td>
        <Td>
          <StatusBadge status={member.status} />
        </Td>
        <Td className="text-right">
          <div className="flex items-center justify-end gap-2">
            <RowActions
              status={member.status}
              onApprove={onApprove}
              onSuspend={onSuspend}
              onReactivate={onReactivate}
            />
            {canDelete && (
              <ActionButton
                onClick={onDelete}
                variant="destructive"
                icon={<Trash2 className="size-3.5" />}
                title="Coordinator only — permanently deletes the member, their login, and dependents"
              >
                Delete
              </ActionButton>
            )}
          </div>
        </Td>
      </tr>

      {expanded && familyCount > 0 && (
        <tr id={`family-${member.id}`} className="bg-stone-50">
          <td colSpan={7} className="px-4 pb-4">
            <DependentsPanel dependents={member.dependents} />
          </td>
        </tr>
      )}
    </>
  );
}

function DependentsPanel({ dependents }: { dependents: DependentRow[] }) {
  return (
    <div className="ms-6 rounded-md border border-stone-200 bg-white">
      <div className="border-b border-stone-200 px-3 py-2 text-xs font-medium uppercase tracking-wide text-stone-500">
        Household members ({dependents.length})
      </div>
      <ul className="divide-y divide-stone-100">
        {dependents.map((dep) => (
          <li key={dep.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
            <div>
              <span className="font-medium text-stone-900">
                {dep.firstName} {dep.lastName}
              </span>
              {dep.relationship && (
                <span className="ms-2 inline-flex rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
                  {prettyRelationship(dep.relationship)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-stone-500">
              {dep.dateOfBirth && (
                <span>DOB {new Date(dep.dateOfBirth).toLocaleDateString()}</span>
              )}
              {dep.phone && <span>{dep.phone}</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function prettyRelationship(value: string): string {
  // Strawberry emits enum NAMES uppercase ("SPOUSE", "CHILD", …) on the wire
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function RowActions({
  status,
  onApprove,
  onSuspend,
  onReactivate,
}: {
  status: StatusValue;
  onApprove: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
}) {
  if (status === "PENDING_VERIFICATION") {
    return (
      <ActionButton onClick={onApprove} variant="primary" icon={<Check className="size-3.5" />}>
        Approve
      </ActionButton>
    );
  }
  if (status === "ACTIVE") {
    return (
      <ActionButton onClick={onSuspend} variant="danger" icon={<ShieldAlert className="size-3.5" />}>
        Suspend
      </ActionButton>
    );
  }
  // Lapsed or suspended
  return (
    <ActionButton onClick={onReactivate} variant="neutral" icon={<RotateCcw className="size-3.5" />}>
      Reactivate
    </ActionButton>
  );
}

function ActionButton({
  onClick,
  children,
  variant,
  icon,
  title,
}: {
  onClick: () => void;
  children: React.ReactNode;
  variant: "primary" | "danger" | "neutral" | "destructive";
  icon: React.ReactNode;
  title?: string;
}) {
  const variants = {
    primary: "bg-green-600 text-white hover:bg-green-700",
    danger: "bg-red-600 text-white hover:bg-red-700",
    neutral: "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50",
    // 'destructive' is a darker, more deliberate red than 'danger' — used for
    // irreversible delete actions so it visually distinguishes from suspend.
    destructive: "border border-red-700 bg-red-700 text-white hover:bg-red-800",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
        variants[variant],
      )}
    >
      {icon}
      {children}
    </button>
  );
}

const STATUS_STYLES: Record<StatusValue, string> = {
  PENDING_VERIFICATION: "bg-gold-100 text-gold-800",
  ACTIVE:               "bg-green-100 text-green-800",
  LAPSED:               "bg-stone-100 text-stone-700",
  SUSPENDED:            "bg-red-100 text-red-800",
};

const STATUS_LABEL: Record<StatusValue, string> = {
  PENDING_VERIFICATION: "Pending",
  ACTIVE:               "Active",
  LAPSED:               "Lapsed",
  SUSPENDED:            "Suspended",
};

function StatusBadge({ status }: { status: StatusValue }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
        STATUS_STYLES[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-4 py-2", className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-4 py-3 align-top", className)}>{children}</td>;
}

function StatCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: number;
  hint?: string;
  accent?: "primary";
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-white p-4",
        accent === "primary" ? "border-red-200 bg-red-50/40" : "border-stone-200",
      )}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-stone-500">{label}</div>
      <div
        className={cn(
          "mt-1 text-3xl font-semibold tabular-nums",
          accent === "primary" ? "text-flag-red" : "text-stone-900",
        )}
      >
        {value.toLocaleString()}
      </div>
      {hint && <div className="mt-0.5 text-xs text-stone-500">{hint}</div>}
    </div>
  );
}
